import './PersonalData.scss';

import React, { useState } from 'react';
import { ID } from '../../fields/ID/ID';
import { Name } from '../../fields/Name/Name';
import { LastName } from '../../fields/LastName/LastName';
import { SecondLastName } from '../../fields/SecondLastName/SecondLastName';
import { BirthDate } from '../../fields/BirthDate/BirthDate';
import { Resident } from '../../fields/Resident/Resident';
import { Section } from '../../Section/Section';
import { FormattedText, formatText } from 'core/i18n';
import { mustShowInfoAnotherAccount, mustShowTreatmentFields } from 'utilities/logicByCountry';
import { JointHolderContainer } from '../../JointHolderContainer';
import { ResidentContainer } from '../../ResidentContainer';
import { OlderContainer } from '../../OlderContainer';
import { doTagButtonUnderstood } from '../../tagging';
import { PUBLICA_URL } from 'constants/index';
import { Nationality } from '../../fields/Nationality/Nationality';
import { isCountrySpain } from 'utilities/multiLanguageHelper';
import { SECTION_TYPE } from '../../constants';
import { Treatment } from 'components/newAccountSteps/initialStep/fields/Treatment/Treatment';
import { InputPassport } from 'components/shared/InputPassport/InputPassport';
import { usePassport } from 'components/newAccountSteps/initialStep/sections/PersonalData/hooks/usePassport';
import { getIsSelectedCountryDifferent } from 'components/newAccountSteps/initialStep/sections/PersonalData/utils/getIsSelectedCountryDifferent';
import {
    doTagBirthDay,
    doTagBirthMonth,
    doTagBirthYear,
    doTagID,
    doTagIsResident,
    doTagLastName,
    doTagName,
    doTagNationalityClose,
    doTagNationalityOpen,
    doTagSecondLastName,
    doTagTin,
} from './tagging';

import { useNationalityOnChange } from './hooks/useNationalityOnChange';
import { usePersonalDataValidations } from './hooks/usePersonalDataValidations';
import { KeepInMindMessage } from './KeepInMindMessage/KeepInMindMessage';
import VulnerablyOlderAdvise from './VulnerablyOlderAdvise/VulnerablyOlderAdvise';
import { useInitialStepForm } from '../../InitialStepForm/context/InitialStepFormContext/InitialStepFormContext';

export const PersonalData = ({
    userProfile,
    formActions,
    userProfileForm,
    actions,
    focus,
    options,
    optionsActions,
    promoCode,
    isCollapsed,
    isValidated,
    handleModifySection,
    handleValidateSection,
}) => {
    const {
        isAdviseShown,
        shouldShowJointHolderDocAdvise,
        shouldShowNotResidentAdvise,
        shouldShowUnderageAdvise,
        shouldShowVulnerablyOlderAdvise,
    } = useInitialStepForm();

    const { documentType, nationality, isValidatingTin } = userProfile;
    const isPassportRequired = usePassport({
        countries: options.get('countries'),
        nationality,
        documentType,
        formActions,
    });

    const [isResident, setIsResident] = useState(false);
    const [showLastNameDisclaimer, setShowLastNameDisclaimer] = useState(false);

    const [hideBirthDateTextErrors, setHideBirthDateTextErrors] = useState(false);

    const {
        onValidateSection,
        handleValidateBirthDate,
        handleSecondLastNameValidation,
        handleValidateResident,
        isFormWithErrors,
        isValidDate,
    } = usePersonalDataValidations({
        userProfile,
        userProfileForm,
        formActions,
        isValidatingTin,
        isPassportRequired,
        isResident,
        handleValidateSection,
    });

    const { handleChangeNationality } = useNationalityOnChange({ userProfile, formActions });

    const onHandleClickRedirects = () => {
        doTagButtonUnderstood();
        window.location.href = PUBLICA_URL;
    };

    const handleOnChangeResident = value => {
        const isValueTruthy = value === 'true';
        setIsResident(isValueTruthy);
        doTagIsResident(isValueTruthy);
    };

    const handleBirthDateOnFocus = () => {
        setHideBirthDateTextErrors(true);
    };

    const handleBirthDateOnBlur = () => {
        setHideBirthDateTextErrors(false);
    };

    return (
        <Section
            isButtonDisabled={isFormWithErrors}
            hideFooter={isAdviseShown}
            type={SECTION_TYPE.PERSONAL_DATA}
            isCollapsed={isCollapsed}
            isValidated={isValidated}
            handleModifySection={handleModifySection}
            handleValidateSection={onValidateSection}
            userProfile={userProfile}
            promoCode={promoCode}
        >
            <Nationality
                options={options}
                optionsActions={optionsActions}
                onChange={handleChangeNationality}
                onOpen={doTagNationalityOpen}
                onClose={doTagNationalityClose}
            />
            <div className="document-id-with-advise">
                <ID
                    userProfile={userProfile}
                    focus={focus}
                    userProfileForm={userProfileForm}
                    formActions={formActions}
                    validateSecondLastName={handleSecondLastNameValidation}
                    disabled={!nationality}
                    isSelectorDisabled={isCountrySpain() || !nationality || getIsSelectedCountryDifferent(nationality)}
                    onFocus={doTagID}
                />
                <KeepInMindMessage
                    nationality={nationality}
                    documentType={documentType}
                    isCountrySelectedFromEuropeanUnion={optionsActions.isCountrySelectedFromEuropeanUnion()}
                />
            </div>
            {isPassportRequired && (
                <div className="row">
                    <div className="col-sm-6">
                        <InputPassport
                            label={formatText('field-passport')}
                            userProfile={userProfile}
                            userProfileForm={userProfileForm}
                            formActions={formActions}
                            isRequired={isPassportRequired}
                        />
                    </div>
                </div>
            )}
            <Treatment
                userProfile={userProfile}
                userProfileForm={userProfileForm}
                formActions={formActions}
                focus={focus}
                hidden={!mustShowTreatmentFields()}
            />
            <Name
                formActions={formActions}
                userProfile={userProfile}
                userProfileForm={userProfileForm}
                onFocus={doTagName}
            />
            <LastName
                formActions={formActions}
                userProfile={userProfile}
                userProfileForm={userProfileForm}
                showDisclaimer={showLastNameDisclaimer}
                setShowDisclaimer={setShowLastNameDisclaimer}
                onFocus={doTagLastName}
            />
            <SecondLastName
                userProfile={userProfile}
                userProfileForm={userProfileForm}
                formActions={formActions}
                onFocus={doTagSecondLastName}
            />
            <div className={shouldShowJointHolderDocAdvise ? 'hidden' : ''}>
                <BirthDate
                    userProfile={userProfile}
                    userProfileForm={userProfileForm}
                    formActions={formActions}
                    isValidDate={isValidDate}
                    validateGroups={handleValidateBirthDate}
                    onFocusBirthDay={doTagBirthDay}
                    onFocusBirthMonth={doTagBirthMonth}
                    onFocusBirthYear={doTagBirthYear}
                    onFocus={handleBirthDateOnFocus}
                    onBlur={handleBirthDateOnBlur}
                    hideTextErrors={hideBirthDateTextErrors}
                />
                <div className={shouldShowUnderageAdvise || shouldShowVulnerablyOlderAdvise ? 'hidden' : ''}>
                    <Resident
                        userProfile={userProfile}
                        userProfileForm={userProfileForm}
                        formActions={formActions}
                        actions={actions}
                        handleValidateResident={handleValidateResident}
                        onChange={handleOnChangeResident}
                        onFocusTin={doTagTin}
                    />
                </div>
            </div>
            {mustShowInfoAnotherAccount() && (
                <FormattedText value="field-infoAnotherAccount" className="info-another-account" />
            )}
            <ResidentContainer
                visibility={shouldShowNotResidentAdvise}
                handleOnClick={onHandleClickRedirects}
                id="countryRestriction"
            />
            <VulnerablyOlderAdvise visibility={shouldShowVulnerablyOlderAdvise} onClick={onHandleClickRedirects} />
            <OlderContainer
                visibility={shouldShowUnderageAdvise}
                handleOnClick={onHandleClickRedirects}
                id="ageRestriction"
            />
            <JointHolderContainer
                visibility={shouldShowJointHolderDocAdvise}
                handleOnClick={onHandleClickRedirects}
                id="JointHolderContainer"
            />
        </Section>
    );
};
